import React, { useState, useEffect, useMemo } from 'react';
import { PageLoading } from '../../../components/ui/Skeleton';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import Icon from '../../../components/AppIcon';
import { adminService } from '../../../services/adminService';
import { gaAnalyticsService } from '../../../services/gaAnalyticsService';
import { useTranslation } from 'react-i18next';
import { categorical, categoricalPalette, semanticColors, statusColor, chartChrome } from '../../../utils/chartTheme';

// ---------- Colour ----------
// Resolved from the theme rather than hardcoded, so these follow light/dark and
// agree with the badges elsewhere in the app. See utils/chartTheme.js.
const paletteForTheme = () => {
  const c = semanticColors();
  return {
    primary: c.primary,
    primaryLight: categorical(6),
    accent: c.accent,
    accentLight: categorical(3),
    success: c.success,
    successLight: categorical(2),
    warning: c.warning,
    warningLight: categorical(1),
    error: c.error,
    errorLight: categorical(5),
    rose: categorical(5),
    sky: c.sky,
  };
};

const piePalette = () => categoricalPalette();


// ---------- Custom Tooltip ----------
const ChartTooltip = ({ active, payload, label, valueLabel }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-xl shadow-elevation-3 px-4 py-3 min-w-[160px]">
      <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center justify-between gap-4 py-0.5">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5" style={{ background: entry.color }} />
            <span className="text-sm text-foreground">{entry.name}</span>
          </div>
          <span className="text-sm font-bold text-foreground">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-card border border-border rounded-xl shadow-elevation-3 px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="w-3 h-3" style={{ background: d.payload?.fill || d.color }} />
        <span className="text-sm font-medium text-foreground capitalize">{d.name?.replace(/_/g, ' ')}</span>
      </div>
      <p className="text-lg font-bold text-foreground mt-1">{d.value}</p>
    </div>
  );
};

// ---------- Chart Card wrapper ----------
const ChartCard = ({ title, subtitle, icon, children, className = '' }) => (
  <div className={`bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden ${className}`}>
    <div className="px-6 pt-5 pb-2 flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon name={icon} size={18} className="text-primary" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
    <div className="px-4 pb-5">
      {children}
    </div>
  </div>
);

// ---------- Stat Card ----------
const StatCard = ({ label, value, icon, color, trend, trendLabel }) => (
  <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-5 hover:shadow-elevation-3 transition-shadow duration-300">
    <div className="flex items-center justify-between mb-3">
      <div className={`w-11 h-11 rounded-xl ${color} flex items-center justify-center`}>
        <Icon name={icon} size={22} className="text-white" />
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg ${
          trend >= 0 ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
        }`}>
          <Icon name={trend >= 0 ? 'TrendingUp' : 'TrendingDown'} size={14} />
          <span>{trend >= 0 ? '+' : ''}{trend}</span>
        </div>
      )}
    </div>
    <p className="text-3xl font-extrabold text-foreground tracking-tight">{value}</p>
    <p className="text-xs font-medium text-muted-foreground mt-1">{label}</p>
    {trendLabel && <p className="text-[11px] text-muted-foreground mt-0.5">{trendLabel}</p>}
  </div>
);

// ---------- Custom Legend ----------
const CustomPieLegend = ({ data, colors }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2 px-2 justify-center">
    {data.map((entry, i) => (
      <div key={entry.name} className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 flex-shrink-0" style={{ background: colors[i % colors.length] }} />
        <span className="text-xs text-muted-foreground capitalize">{entry.name?.replace(/_/g, ' ')}</span>
        <span className="text-xs font-semibold text-foreground">{entry.value}</span>
      </div>
    ))}
  </div>
);

// ---------- Main Component ----------
const AnalyticsDashboard = () => {
  // Recomputed per render so a theme switch repaints the charts with it.
  const COLORS = paletteForTheme();
  const PIE_PALETTE = piePalette();
  const { t } = useTranslation('admin');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeChart, setActiveChart] = useState('overview');

  // Google Analytics state
  const [gaData, setGaData] = useState(null);
  const [gaLoading, setGaLoading] = useState(false);
  const [gaError, setGaError] = useState('');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await adminService?.getAnalyticsData();
      setData(result);
    } catch (err) {
      setError(err?.message || t('analytics.errors.failedToLoad', 'Failed to load analytics data'));
    } finally {
      setLoading(false);
    }
  };

  const loadGaAnalytics = async () => {
    try {
      setGaLoading(true);
      setGaError('');
      const result = await gaAnalyticsService?.getAnalytics();
      setGaData(result);
    } catch (err) {
      setGaError(err?.message || 'Failed to load web traffic data');
    } finally {
      setGaLoading(false);
    }
  };

  // Load GA data when switching to web traffic tab
  useEffect(() => {
    if (activeChart === 'webTraffic' && !gaData && !gaLoading) {
      loadGaAnalytics();
    }
  }, [activeChart]);

  // Compute month-over-month trends
  const trends = useMemo(() => {
    if (!data?.monthlyTrends || data.monthlyTrends.length < 2) return {};
    const curr = data.monthlyTrends[data.monthlyTrends.length - 1];
    const prev = data.monthlyTrends[data.monthlyTrends.length - 2];
    return {
      users: curr.users - prev.users,
      submissions: curr.submissions - prev.submissions,
      reviews: curr.reviews - prev.reviews,
      conferences: curr.conferences - prev.conferences,
    };
  }, [data]);

  const chartViews = [
    { id: 'overview', label: t('analytics.views.overview', 'Overview'), icon: 'BarChart3' },
    { id: 'growth', label: t('analytics.views.growth', 'User Growth'), icon: 'TrendingUp' },
    { id: 'submissions', label: t('analytics.views.submissions', 'Submissions'), icon: 'FileText' },
    { id: 'reviews', label: t('analytics.views.reviews', 'Reviews'), icon: 'ClipboardCheck' },
    { id: 'webTraffic', label: t('analytics.views.webTraffic', 'Web Traffic'), icon: 'Globe' },
  ];

  if (loading) {
    return (
      <div className="p-6">
        <PageLoading label={t('analytics.loading', 'Loading analytics...')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-20">
          <Icon name="AlertTriangle" size={48} className="text-error mx-auto mb-4" />
          <p className="text-error font-medium mb-4">{error}</p>
          <button onClick={loadAnalytics} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
            {t('analytics.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const getStatusColor = (name) => statusColor(name);
  const submissionPieColors = data.submissionStatusData.map(d => getStatusColor(d.name));
  const conferencePieColors = data.conferenceStatusData.map(d => getStatusColor(d.name));
  const reviewPieColors = data.reviewDecisionData.map(d => getStatusColor(d.name));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-foreground">{t('analytics.title', 'Platform Analytics')}</h2>
          <p className="text-sm text-muted-foreground">{t('analytics.subtitle', 'Insights and trends across the platform')}</p>
        </div>
        <button
          onClick={loadAnalytics}
          className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/70 text-foreground rounded-xl text-sm font-medium transition-colors self-start"
        >
          <Icon name="RefreshCw" size={15} />
          {t('analytics.refresh', 'Refresh')}
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label={t('analytics.stats.totalUsers', 'Total Users')}
          value={data.totals.users}
          icon="Users"
          color="bg-primary"
          trend={trends.users}
          trendLabel={t('analytics.stats.vsLastMonth', 'vs last month')}
        />
        <StatCard
          label={t('analytics.stats.totalSubmissions', 'Total Submissions')}
          value={data.totals.submissions}
          icon="FileText"
          color="bg-success"
          trend={trends.submissions}
          trendLabel={t('analytics.stats.vsLastMonth', 'vs last month')}
        />
        <StatCard
          label={t('analytics.stats.totalConferences', 'Total Conferences')}
          value={data.totals.conferences}
          icon="FolderOpen"
          color="bg-accent"
          trend={trends.conferences}
          trendLabel={t('analytics.stats.vsLastMonth', 'vs last month')}
        />
        <StatCard
          label={t('analytics.stats.totalReviews', 'Total Reviews')}
          value={data.totals.reviews}
          icon="ClipboardCheck"
          color="bg-warning"
          trend={trends.reviews}
          trendLabel={t('analytics.stats.vsLastMonth', 'vs last month')}
        />
      </div>

      {/* Chart view selector */}
      <div className="flex gap-1.5 p-1 bg-muted rounded-xl w-fit">
        {chartViews.map(view => (
          <button
            key={view.id}
            onClick={() => setActiveChart(view.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              activeChart === view.id
                ? 'bg-card text-foreground shadow-elevation-1'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={view.icon} size={15} />
            <span className="hidden sm:inline">{view.label}</span>
          </button>
        ))}
      </div>

      {/* ===== OVERVIEW VIEW ===== */}
      {activeChart === 'overview' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          {/* Activity Trends — large chart */}
          <ChartCard
            title={t('analytics.charts.activityTrends', 'Activity Trends')}
            subtitle={t('analytics.charts.last12Months', 'Last 12 months')}
            icon="Activity"
            className="xl:col-span-2"
          >
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={data.monthlyTrends} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradSubs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.success} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={COLORS.success} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradReviews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.accent} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={COLORS.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.5} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Area type="monotone" dataKey="users" name={t('analytics.legend.registrations', 'Registrations')} stroke={COLORS.primary} strokeWidth={2.5} fill="url(#gradUsers)" dot={false} activeDot={{ r: 5, strokeWidth: 2 }} />
                <Area type="monotone" dataKey="submissions" name={t('analytics.legend.submissions', 'Submissions')} stroke={COLORS.success} strokeWidth={2.5} fill="url(#gradSubs)" dot={false} activeDot={{ r: 5, strokeWidth: 2 }} />
                <Area type="monotone" dataKey="reviews" name={t('analytics.legend.reviews', 'Reviews')} stroke={COLORS.accent} strokeWidth={2.5} fill="url(#gradReviews)" dot={false} activeDot={{ r: 5, strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Submission status pie */}
          <ChartCard
            title={t('analytics.charts.submissionStatus', 'Submission Status')}
            subtitle={t('analytics.charts.distribution', 'Distribution')}
            icon="PieChart"
          >
            {data.submissionStatusData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={data.submissionStatusData}
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                      stroke="none"
                    >
                      {data.submissionStatusData.map((entry, i) => (
                        <Cell key={entry.name} fill={submissionPieColors[i]} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <CustomPieLegend data={data.submissionStatusData} colors={submissionPieColors} />
              </>
            ) : (
              <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">
                {t('analytics.noData', 'No data yet')}
              </div>
            )}
          </ChartCard>
        </div>
      )}

      {/* ===== GROWTH VIEW ===== */}
      {activeChart === 'growth' && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <ChartCard
            title={t('analytics.charts.userGrowth', 'Cumulative User Growth')}
            subtitle={t('analytics.charts.last12Months', 'Last 12 months')}
            icon="TrendingUp"
          >
            <ResponsiveContainer width="100%" height={340}>
              <AreaChart data={data.monthlyTrends} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradCumulative" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.35} />
                    <stop offset="50%" stopColor={COLORS.accent} stopOpacity={0.15} />
                    <stop offset="100%" stopColor={COLORS.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.5} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="cumulativeUsers" name={t('analytics.legend.totalUsers', 'Total Users')} stroke={COLORS.primary} strokeWidth={3} fill="url(#gradCumulative)" dot={false} activeDot={{ r: 6, strokeWidth: 2, fill: COLORS.primary }} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title={t('analytics.charts.monthlyRegistrations', 'Monthly Registrations')}
            subtitle={t('analytics.charts.newUsersPerMonth', 'New users per month')}
            icon="UserPlus"
          >
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={data.monthlyTrends} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.primary} stopOpacity={1} />
                    <stop offset="100%" stopColor={COLORS.accent} stopOpacity={0.8} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.5} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="users" name={t('analytics.legend.newUsers', 'New Users')} fill="url(#gradBar)" radius={[6, 6, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* ===== SUBMISSIONS VIEW ===== */}
      {activeChart === 'submissions' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          <ChartCard
            title={t('analytics.charts.submissionTrend', 'Submission Trend')}
            subtitle={t('analytics.charts.last12Months', 'Last 12 months')}
            icon="FileText"
            className="xl:col-span-2"
          >
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={data.monthlyTrends} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradSubsTrend" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.success} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={COLORS.success} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.5} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="submissions" name={t('analytics.legend.submissions', 'Submissions')} stroke={COLORS.success} strokeWidth={3} fill="url(#gradSubsTrend)" dot={false} activeDot={{ r: 6, strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title={t('analytics.charts.conferenceStatus', 'Conference Status')}
            subtitle={t('analytics.charts.distribution', 'Distribution')}
            icon="FolderOpen"
          >
            {data.conferenceStatusData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={data.conferenceStatusData}
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                      stroke="none"
                    >
                      {data.conferenceStatusData.map((entry, i) => (
                        <Cell key={entry.name} fill={conferencePieColors[i]} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <CustomPieLegend data={data.conferenceStatusData} colors={conferencePieColors} />
              </>
            ) : (
              <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">
                {t('analytics.noData', 'No data yet')}
              </div>
            )}
          </ChartCard>

          {/* Top conferences bar chart */}
          {data.topConferences.length > 0 && (
            <ChartCard
              title={t('analytics.charts.topConferences', 'Top Conferences')}
              subtitle={t('analytics.charts.bySubmissions', 'By submission count')}
              icon="Award"
              className="xl:col-span-3"
            >
              <ResponsiveContainer width="100%" height={Math.max(200, data.topConferences.length * 44)}>
                <BarChart data={data.topConferences} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="gradTopConf" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={COLORS.primary} stopOpacity={1} />
                      <stop offset="100%" stopColor={COLORS.accent} stopOpacity={0.85} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.5} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: 'var(--color-foreground)', fontWeight: 500 }} axisLine={false} tickLine={false} width={120} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="submissions" name={t('analytics.legend.submissions', 'Submissions')} fill="url(#gradTopConf)" radius={[0, 6, 6, 0]} maxBarSize={32} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}

      {/* ===== REVIEWS VIEW ===== */}
      {activeChart === 'reviews' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          <ChartCard
            title={t('analytics.charts.reviewActivity', 'Review Activity')}
            subtitle={t('analytics.charts.last12Months', 'Last 12 months')}
            icon="ClipboardCheck"
            className="xl:col-span-2"
          >
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.monthlyTrends} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradReviewBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.accent} stopOpacity={1} />
                    <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0.7} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.5} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="reviews" name={t('analytics.legend.reviews', 'Reviews')} fill="url(#gradReviewBar)" radius={[6, 6, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title={t('analytics.charts.reviewDecisions', 'Review Decisions')}
            subtitle={t('analytics.charts.distribution', 'Distribution')}
            icon="Scale"
          >
            {data.reviewDecisionData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={data.reviewDecisionData}
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                      stroke="none"
                    >
                      {data.reviewDecisionData.map((entry, i) => (
                        <Cell key={entry.name} fill={reviewPieColors[i]} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <CustomPieLegend data={data.reviewDecisionData} colors={reviewPieColors} />
              </>
            ) : (
              <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">
                {t('analytics.noData', 'No data yet')}
              </div>
            )}
          </ChartCard>
        </div>
      )}

      {/* ===== WEB TRAFFIC VIEW (Google Analytics) ===== */}
      {activeChart === 'webTraffic' && (
        <div className="space-y-5">
          {gaLoading && (
            <PageLoading label={t('analytics.ga.loading', 'Loading web traffic data...')} />
          )}

          {gaError && (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-lg bg-warning/10 flex items-center justify-center mx-auto mb-4">
                <Icon name="Globe" size={32} className="text-warning" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">{t('analytics.ga.setupRequired', 'Google Analytics Setup Required')}</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                {t('analytics.ga.setupDescription', 'To display web traffic data, configure your Google Analytics service account in the Supabase Edge Function.')}
              </p>
              <div className="bg-muted rounded-xl p-4 max-w-lg mx-auto text-start space-y-2">
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">{t('analytics.ga.setupSteps', 'Setup Steps')}</p>
                <div className="space-y-1.5 text-sm text-muted-foreground">
                  <p>1. {t('analytics.ga.step1', 'Create a Google Cloud service account')}</p>
                  <p>2. {t('analytics.ga.step2', 'Enable the GA4 Data API')}</p>
                  <p>3. {t('analytics.ga.step3', 'Add service account to your GA4 property')}</p>
                  <p>4. {t('analytics.ga.step4', 'Set Supabase secrets: GA_SERVICE_ACCOUNT & GA_PROPERTY_ID')}</p>
                </div>
              </div>
              <button onClick={loadGaAnalytics} className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                {t('analytics.retry', 'Retry')}
              </button>
            </div>
          )}

          {gaData && !gaLoading && (
            <>
              {/* GA Stat Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  label={t('analytics.ga.activeUsers', 'Active Users')}
                  value={gaData.overview?.activeUsers?.toLocaleString() || '0'}
                  icon="Users"
                  color="bg-primary"
                  trend={gaData.overview?.prev ? Math.round(((gaData.overview.activeUsers - gaData.overview.prev.activeUsers) / (gaData.overview.prev.activeUsers || 1)) * 100) : undefined}
                  trendLabel={t('analytics.ga.vs28Days', 'vs previous 28 days')}
                />
                <StatCard
                  label={t('analytics.ga.pageViews', 'Page Views')}
                  value={gaData.overview?.pageViews?.toLocaleString() || '0'}
                  icon="Eye"
                  color="bg-success"
                  trend={gaData.overview?.prev ? Math.round(((gaData.overview.pageViews - gaData.overview.prev.pageViews) / (gaData.overview.prev.pageViews || 1)) * 100) : undefined}
                  trendLabel={t('analytics.ga.vs28Days', 'vs previous 28 days')}
                />
                <StatCard
                  label={t('analytics.ga.sessions', 'Sessions')}
                  value={gaData.overview?.sessions?.toLocaleString() || '0'}
                  icon="MousePointerClick"
                  color="bg-accent"
                  trend={gaData.overview?.prev ? Math.round(((gaData.overview.sessions - gaData.overview.prev.sessions) / (gaData.overview.prev.sessions || 1)) * 100) : undefined}
                  trendLabel={t('analytics.ga.vs28Days', 'vs previous 28 days')}
                />
                <StatCard
                  label={t('analytics.ga.bounceRate', 'Bounce Rate')}
                  value={`${(gaData.overview?.bounceRate * 100)?.toFixed(1) || '0'}%`}
                  icon="LogOut"
                  color="bg-warning"
                />
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                {/* Daily traffic trend — large chart */}
                <ChartCard
                  title={t('analytics.ga.dailyTraffic', 'Daily Traffic')}
                  subtitle={t('analytics.ga.last30Days', 'Last 30 days')}
                  icon="Activity"
                  className="xl:col-span-2"
                >
                  <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={gaData.dailyTrends} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gradGaViews" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradGaUsers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={COLORS.accent} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={COLORS.accent} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.5} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                      <Area type="monotone" dataKey="pageViews" name={t('analytics.ga.pageViews', 'Page Views')} stroke={COLORS.primary} strokeWidth={2.5} fill="url(#gradGaViews)" dot={false} activeDot={{ r: 5, strokeWidth: 2 }} />
                      <Area type="monotone" dataKey="users" name={t('analytics.ga.visitors', 'Visitors')} stroke={COLORS.accent} strokeWidth={2.5} fill="url(#gradGaUsers)" dot={false} activeDot={{ r: 5, strokeWidth: 2 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* Device breakdown pie */}
                <ChartCard
                  title={t('analytics.ga.devices', 'Devices')}
                  subtitle={t('analytics.ga.bySessionCount', 'By session count')}
                  icon="Smartphone"
                >
                  {gaData.devices?.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie
                            data={gaData.devices}
                            cx="50%" cy="50%"
                            innerRadius={55} outerRadius={85}
                            paddingAngle={3}
                            dataKey="value"
                            stroke="none"
                          >
                            {gaData.devices.map((entry, i) => (
                              <Cell key={entry.name} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                            ))}
                          </Pie>
                          <Tooltip content={<PieTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <CustomPieLegend data={gaData.devices} colors={PIE_PALETTE} />
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">
                      {t('analytics.noData', 'No data yet')}
                    </div>
                  )}
                </ChartCard>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                {/* Traffic sources bar chart */}
                <ChartCard
                  title={t('analytics.ga.trafficSources', 'Traffic Sources')}
                  subtitle={t('analytics.ga.byChannel', 'By channel group')}
                  icon="Share2"
                >
                  {gaData.trafficSources?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={Math.max(200, gaData.trafficSources.length * 44)}>
                      <BarChart data={gaData.trafficSources} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                        <defs>
                          <linearGradient id="gradTraffic" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor={COLORS.success} stopOpacity={1} />
                            <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0.85} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.5} horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: 'var(--color-foreground)', fontWeight: 500 }} axisLine={false} tickLine={false} width={120} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="sessions" name={t('analytics.ga.sessions', 'Sessions')} fill="url(#gradTraffic)" radius={[0, 6, 6, 0]} maxBarSize={32} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
                      {t('analytics.noData', 'No data yet')}
                    </div>
                  )}
                </ChartCard>

                {/* Top pages table */}
                <ChartCard
                  title={t('analytics.ga.topPages', 'Top Pages')}
                  subtitle={t('analytics.ga.last28Days', 'Last 28 days')}
                  icon="FileText"
                >
                  {gaData.topPages?.length > 0 ? (
                    <div className="space-y-1 max-h-[340px] overflow-y-auto">
                      {gaData.topPages.map((page, i) => (
                        <div key={page.page} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors">
                          <span className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">
                            {i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{page.page}</p>
                            <p className="text-xs text-muted-foreground">{page.users?.toLocaleString()} {t('analytics.ga.visitors', 'visitors')}</p>
                          </div>
                          <span className="text-sm font-bold text-foreground flex-shrink-0">{page.views?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
                      {t('analytics.noData', 'No data yet')}
                    </div>
                  )}
                </ChartCard>
              </div>

              {/* Countries */}
              {gaData.countries?.length > 0 && (
                <ChartCard
                  title={t('analytics.ga.topCountries', 'Top Countries')}
                  subtitle={t('analytics.ga.byActiveUsers', 'By active users')}
                  icon="MapPin"
                >
                  <ResponsiveContainer width="100%" height={Math.max(200, gaData.countries.length * 44)}>
                    <BarChart data={gaData.countries} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                      <defs>
                        <linearGradient id="gradCountries" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor={COLORS.accent} stopOpacity={1} />
                          <stop offset="100%" stopColor={COLORS.sky} stopOpacity={0.85} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.5} horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: 'var(--color-foreground)', fontWeight: 500 }} axisLine={false} tickLine={false} width={120} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="users" name={t('analytics.ga.activeUsers', 'Active Users')} fill="url(#gradCountries)" radius={[0, 6, 6, 0]} maxBarSize={32} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
